import {
  BankIcon,
  CashSpecialIcon,
  Title,
  WalletIcon,
  AddCardIcon,
} from "lola-framework-ui-test";

import { useCallback, type JSX, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useStore } from "../store/globalState";
import { getBrandInfo } from "../constants/card-brands";
import { PayoutMethodType } from "../interfaces/payout";
import {
  destinationListKey,
  type CompatibleBookDestination,
} from "../utils/recipient-destinations";
import { PayoutInfo } from "./PayoutInfo";
import masterCardLogo from "../assets/master_card_icon.svg";
import visaLogo from "../assets/visa_color_icon.svg";
import { ScrollFadeIndicator } from "./ScrollFadeIndicator";
import { formatBankAccountLabel } from "../utils/bankAccountLabel";
import { CloseLight } from "../assets/navIcons";

const TYPE_ROW_ORDER: PayoutMethodType[] = [
  PayoutMethodType.CASH_PICKUP,
  PayoutMethodType.MOBILE_WALLET,
];
const PICKER_SKELETON_ROWS = 3;

const PickerListSkeleton = () => (
  <>
    {Array.from({ length: PICKER_SKELETON_ROWS }, (_, i) => (
      <div key={i} className="payout-skeleton" aria-hidden="true">
        <span className="payout-skeleton-icon" />
        <span className="payout-skeleton-lines">
          <span className="payout-skeleton-line" />
          <span className="payout-skeleton-line short" />
        </span>
      </div>
    ))}
  </>
);

export type FlatPayoutPick =
  | { kind: "card"; entry: CompatibleBookDestination }
  | { kind: "bank"; entry: CompatibleBookDestination }
  | { kind: "method"; method: PayoutMethodType }
  | { kind: "add" }
  | { kind: "addBank" };

export interface SelectPayoutMethodSlotProps {
  selectedMethod?: PayoutMethodType | null;
  selectedPaymentMethodId?: string | null;
  available: PayoutMethodType[];
  cardEntries: CompatibleBookDestination[];
  bankEntries: CompatibleBookDestination[];
  /** Pre-quote still unresolved — full-list shimmer. */
  loading?: boolean;
  /** Rows rendered first in the picker, before saved/catalog methods. */
  prependRows?: ReactNode;
  onPick: (pick: FlatPayoutPick) => void;
  onClose: () => void;
}

/**
 * Flat payout picker: recipient-book PUSH_TO_CARD and BANK_ACCOUNT destinations
 * + type rows that still need a details popup + add CTAs. No type rows for
 * card or bank.
 */
export const SelectPayoutMethodSlot: React.FC<SelectPayoutMethodSlotProps> = ({
  selectedMethod = null,
  selectedPaymentMethodId = null,
  available,
  cardEntries,
  bankEntries,
  loading = false,
  prependRows = null,
  onPick,
  onClose,
}) => {
  const { t } = useTranslation();
  const { theme } = useStore();
  const colorConfig = theme.whiteView;
  const { bodyCopy, backgroundBtn, textColorBtn } = colorConfig;

  const availableSet = new Set(available);
  const cardOk = availableSet.has(PayoutMethodType.PUSH_TO_CARD);
  const bankOk = availableSet.has(PayoutMethodType.BANK_ACCOUNT);
  const typeRows = TYPE_ROW_ORDER.filter((m) => availableSet.has(m));
  const cardSubtitle = t(`payout.methods.${PayoutMethodType.PUSH_TO_CARD}`);
  const bankSubtitle = t(`payout.methods.${PayoutMethodType.BANK_ACCOUNT}`);

  const iconFor = (type: PayoutMethodType): JSX.Element => {
    switch (type) {
      case PayoutMethodType.CASH_PICKUP:
        return <CashSpecialIcon size={22} colors={[bodyCopy, bodyCopy]} />;
      default:
        return <WalletIcon size={22} colors={[bodyCopy, bodyCopy]} />;
    }
  };

  const pickIcon = useCallback((brand: string) => {
    let finalIcon: string | null = null;
    if (brand.toLocaleUpperCase().includes("VISA")) finalIcon = visaLogo;
    if (brand.toLocaleUpperCase().includes("MASTERCARD"))
      finalIcon = masterCardLogo;
    if (!finalIcon) finalIcon = getBrandInfo(brand.toLowerCase())?.logo ?? null;
    return finalIcon ? (
      <img src={finalIcon} alt={brand} />
    ) : (
      <span />
    );
  }, []);

  return (
    <section className="select-payer-slot">
      <header className="payout-details-header">
        <span className="payout-header-action" aria-hidden="true" />
        <Title title={t("payout.title")} align="center" color={bodyCopy} />
        <button
          type="button"
          className="payout-header-action payout-close-btn"
          aria-label={t("common.cancel")}
          onClick={onClose}
        >
          <CloseLight size={36} colors={[bodyCopy, bodyCopy]} />
        </button>
      </header>
      <section
        className="select-payer-list select-funding-list"
        aria-busy={loading}
        style={
          {
            "--bgIcon": backgroundBtn,
            "--iconOnBrand": textColorBtn,
          } as React.CSSProperties
        }
      >
        <ScrollFadeIndicator color={bodyCopy} />
        {loading ? <PickerListSkeleton /> : null}
        {!loading ? prependRows : null}
        {!loading && cardOk &&
          cardEntries.map((entry) => {
            const { payout, destination } = entry;
            const last4 = payout.last4;
            const titleText = last4
              ? `${(payout.brand || "CARD").toUpperCase()}-${last4}`
              : t("payout.methods.PUSH_TO_CARD");
            const isSelected =
              Boolean(selectedPaymentMethodId) &&
              selectedPaymentMethodId === payout.paymentMethodId;
            return (
              <PayoutInfo
                key={destinationListKey(destination)}
                title={titleText}
                subtitle={cardSubtitle}
                icon={pickIcon(payout.brand ?? "")}
                className={`${isSelected ? "selected" : ""} funding-neutral-icon`.trim()}
                onChange={() => onPick({ kind: "card", entry })}
              />
            );
          })}
        {!loading && bankOk &&
          bankEntries.map((entry) => {
            const { payout, destination } = entry;
            const last4 = payout.last4;
            const bank = payout.bankId;
            const titleText = last4
              ? (formatBankAccountLabel(bank, last4) ?? `•••• ${last4}`)
              : bank || bankSubtitle;
            const isSelected =
              Boolean(selectedPaymentMethodId) &&
              selectedPaymentMethodId === payout.paymentMethodId;
            return (
              <PayoutInfo
                key={destinationListKey(destination)}
                title={titleText}
                subtitle={bankSubtitle}
                icon={<BankIcon size={22} colors={[bodyCopy, bodyCopy]} />}
                className={`${isSelected ? "selected" : ""} funding-neutral-icon`.trim()}
                onChange={() => onPick({ kind: "bank", entry })}
              />
            );
          })}
        {!loading && typeRows.map((type) => {
          const isSelected = selectedMethod === type;
          return (
            <PayoutInfo
              key={type}
              title={t(`payout.methods.${type}`)}
              subtitle=""
              icon={iconFor(type)}
              className={`${isSelected ? "selected" : ""} funding-neutral-icon`.trim()}
              onChange={() => onPick({ kind: "method", method: type })}
            />
          );
        })}
        {!loading && cardOk && (
          <PayoutInfo
            title={t("payment.addDebitCard")}
            subtitle={cardSubtitle}
            className="funding-brand-icon"
            icon={
              <AddCardIcon size={22} colors={[textColorBtn, textColorBtn]} />
            }
            onChange={() => onPick({ kind: "add" })}
          />
        )}
        {!loading && bankOk && (
          <PayoutInfo
            title={t("payment.addBankAccount")}
            subtitle={bankSubtitle}
            className="funding-brand-icon"
            icon={<BankIcon size={22} colors={[textColorBtn, textColorBtn]} />}
            onChange={() => onPick({ kind: "addBank" })}
          />
        )}
      </section>
    </section>
  );
};
